<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const route = useRoute();
const stickers = ref([]);
const name = ref('');
const file = ref(null);
const fileInput = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const { t } = useI18n();

const endpoint = () =>
  `/api/v1/accounts/${route.params.accountId}/staff_stickers`;

const loadStickers = async () => {
  isLoading.value = true;
  try {
    const response = await window.axios.get(endpoint());
    stickers.value = response.data;
  } catch (error) {
    useAlert(t('STAFF_STICKERS.ERRORS.LOAD'));
  } finally {
    isLoading.value = false;
  }
};

const onFileChange = event => {
  file.value = event.target.files?.[0] || null;
};

const addSticker = async () => {
  if (!name.value.trim() || !file.value) {
    useAlert(t('STAFF_STICKERS.ERRORS.NAME_AND_FILE'));
    return;
  }

  const payload = new FormData();
  payload.append('sticker[name]', name.value.trim());
  payload.append('sticker[file]', file.value);
  isSaving.value = true;
  try {
    await window.axios.post(endpoint(), payload);
    name.value = '';
    file.value = null;
    if (fileInput.value) fileInput.value.value = '';
    await loadStickers();
    useAlert(t('STAFF_STICKERS.SUCCESS.ADDED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('STAFF_STICKERS.ERRORS.ADD'));
  } finally {
    isSaving.value = false;
  }
};

const toggleSticker = async sticker => {
  try {
    const response = await window.axios.patch(`${endpoint()}/${sticker.id}`, {
      sticker: { active: !sticker.active },
    });
    const index = stickers.value.findIndex(item => item.id === sticker.id);
    if (index >= 0) stickers.value.splice(index, 1, response.data);
  } catch (error) {
    useAlert(t('STAFF_STICKERS.ERRORS.UPDATE'));
  }
};

const removeSticker = async sticker => {
  // Native confirmation is required before deleting a sticker.
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(t('STAFF_STICKERS.CONFIRM_REMOVE', { name: sticker.name }))
  )
    return;
  try {
    await window.axios.delete(`${endpoint()}/${sticker.id}`);
    stickers.value = stickers.value.filter(item => item.id !== sticker.id);
  } catch (error) {
    useAlert(t('STAFF_STICKERS.ERRORS.REMOVE'));
  }
};

onMounted(loadStickers);
</script>

<template>
  <SettingsLayout>
    <div class="flex flex-col gap-6 w-full">
      <BaseSettingsHeader
        :title="t('STAFF_STICKERS.TITLE')"
        :description="t('STAFF_STICKERS.DESCRIPTION')"
      />

      <section class="p-6 rounded-xl border border-n-strong bg-n-surface-2">
        <h3 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ t('STAFF_STICKERS.ADD_TITLE') }}
        </h3>
        <p class="mt-1 mb-5 text-sm text-n-slate-11">
          {{ t('STAFF_STICKERS.ADD_DESCRIPTION') }}
        </p>
        <div class="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          <Input
            v-model="name"
            :label="t('STAFF_STICKERS.INTERNAL_NAME')"
            :placeholder="t('STAFF_STICKERS.NAME_PLACEHOLDER')"
          />
          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
              {{ t('STAFF_STICKERS.FILE') }}
            </label>
            <input
              ref="fileInput"
              type="file"
              accept="image/webp,image/png,image/gif,.webp,.png,.gif"
              class="hidden"
              @change="onFileChange"
            />
            <div class="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                :label="t('STAFF_STICKERS.CHOOSE_FILE')"
                @click="fileInput?.click()"
              />
              <span class="truncate text-sm text-n-slate-11">
                {{ file?.name || t('STAFF_STICKERS.NO_FILE_SELECTED') }}
              </span>
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('STAFF_STICKERS.FILE_HELP') }}
            </p>
          </div>
        </div>
        <div class="flex justify-end mt-5">
          <Button
            :label="t('STAFF_STICKERS.ADD_BUTTON')"
            color="teal"
            :is-loading="isSaving"
            @click="addSticker"
          />
        </div>
      </section>

      <section class="p-6 rounded-xl border border-n-strong bg-n-surface-2">
        <h3 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ t('STAFF_STICKERS.CATALOG') }}
        </h3>
        <p v-if="isLoading" class="py-8 text-center text-sm text-n-slate-11">
          {{ t('STAFF_STICKERS.LOADING') }}
        </p>
        <p
          v-else-if="!stickers.length"
          class="py-8 text-center text-sm text-n-slate-11"
        >
          {{ t('STAFF_STICKERS.EMPTY') }}
        </p>
        <div
          v-else
          class="grid grid-cols-1 gap-4 mt-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          <article
            v-for="sticker in stickers"
            :key="sticker.id"
            class="flex items-center gap-3 p-3 rounded-xl border border-n-strong bg-n-alpha-1"
          >
            <img
              :src="sticker.file_url"
              :alt="sticker.name"
              class="size-16 object-contain rounded-lg"
            />
            <div class="min-w-0 flex-1">
              <p class="m-0 truncate font-medium text-n-slate-12">
                {{ sticker.name }}
              </p>
              <button
                type="button"
                class="mt-2 text-xs font-medium"
                :class="sticker.active ? 'text-teal-500' : 'text-n-slate-10'"
                @click="toggleSticker(sticker)"
              >
                {{
                  sticker.active
                    ? t('STAFF_STICKERS.ACTIVE')
                    : t('STAFF_STICKERS.INACTIVE')
                }}
              </button>
              <button
                type="button"
                class="block mt-1 text-xs text-red-500"
                @click="removeSticker(sticker)"
              >
                {{ t('COMMON.REMOVE') }}
              </button>
            </div>
          </article>
        </div>
      </section>
    </div>
  </SettingsLayout>
</template>
